import React, { useMemo } from 'react';
import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  buildPeriodTimeline,
  filterPeriodRecords,
  formatPeriodRange,
  getPeriodMetrics,
} from '../utils/periodStatistics';
import {
  LuActivity,
  LuArrowDownRight,
  LuArrowUpRight,
  LuCalendarDays,
  LuCircleDot,
  LuFrown,
  LuSmile,
} from 'react-icons/lu';
import './PeriodStatistics.css';

const EMOTION_ICONS = {
  happy: LuSmile,
  sad: LuFrown,
  angry: LuActivity,
  fear: LuCircleDot,
  disgust: LuCircleDot,
  surprise: LuCircleDot,
};

function EmotionIcon({ emotion, size = 22 }) {
  const Icon = EMOTION_ICONS[emotion?.key] || LuCircleDot;
  return <Icon size={size} strokeWidth={1.8} aria-hidden="true" />;
}

function Balance({ metrics, title }) {
  return (
    <section className="period-stat__card period-stat__balance">
      <span className="period-stat__eyebrow">EMOTIONAL BALANCE</span>
      <h3>{title}</h3>
      <div className="period-stat__balance-bar" role="img" aria-label={`Positive ${metrics.positivePercentage == null ? 0 : Math.round(metrics.positivePercentage)} percent`}>
        <span style={{ width: `${metrics.positivePercentage || 0}%` }} />
      </div>
      {metrics.positivePercentage == null ? (
        <p>No balance available until a session is recorded.</p>
      ) : (
        <div className="period-stat__balance-labels">
          <span><strong>{metrics.positivePercentage.toFixed(0)}%</strong> positive</span>
          <span><strong>{metrics.negativePercentage.toFixed(0)}%</strong> negative</span>
        </div>
      )}
      <p>Calculated from emotions detected in recorded sessions.</p>
    </section>
  );
}

function EmotionDonut({ metrics }) {
  const data = metrics.distribution;
  return (
    <section className="period-stat__card period-stat__distribution">
      <span className="period-stat__eyebrow">EMOTION MIX</span>
      <h3>Emotional distribution</h3>
      <div className="period-stat__donut">
        <ResponsiveContainer width="50%" height={190}>
          <PieChart>
            <Pie data={data.length ? data : [{ key: 'empty', percentage: 100, color: 'var(--period-empty-chart)', label: 'No recorded data' }]} dataKey="percentage" innerRadius={55} outerRadius={78} paddingAngle={3} stroke="none">
                {(data.length ? data : [{ key: 'empty', color: 'var(--period-empty-chart)' }]).map(item => <Cell key={item.key} fill={item.color} />)}
            </Pie>
            <Tooltip formatter={(value, name) => [`${Number(value).toFixed(0)}%`, name]} contentStyle={{ background: 'var(--period-tooltip-bg)', border: '1px solid var(--period-tooltip-border)', borderRadius: 10, color: 'var(--period-text)' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="period-stat__donut-center">
          {metrics.distribution.length ? (
            <><strong>{metrics.dominant.percentage.toFixed(0)}%</strong><span>{metrics.dominant.label}</span></>
          ) : <span>No recorded data</span>}
        </div>
        <div className="period-stat__legend">
          {metrics.distribution.length ? metrics.distribution.map(item => (
            <div key={item.key}>
              <span><i style={{ background: item.color }} /><EmotionIcon emotion={item} size={15} />{item.label}</span>
              <strong>{item.percentage.toFixed(0)}%</strong>
            </div>
          )) : <p>No recorded emotions for this period.</p>}
        </div>
      </div>
    </section>
  );
}

export default function PeriodStatistics({ period, aggregate, previousAggregate, records = [], startDate, endDate }) {
  const scopedRecords = useMemo(
    () => filterPeriodRecords(records, endDate, period),
    [records, endDate, period],
  );
  const metrics = useMemo(
    () => getPeriodMetrics(aggregate, scopedRecords.length ? scopedRecords : records),
    [aggregate, records, scopedRecords],
  );
  const timeline = useMemo(() => buildPeriodTimeline(scopedRecords, period), [scopedRecords, period]);
  const previous = useMemo(() => getPeriodMetrics(previousAggregate, []), [previousAggregate]);
  const comparison = previousAggregate?.top_5
    ? metrics.dominant.key === previous.dominant.key
      ? `Same dominant emotion as last ${period === 'yearly' ? 'year' : 'month'}`
      : `Previously ${previous.dominant.label}`
    : null;
  const comparisonScore = previousAggregate?.top_5 && metrics.positivePercentage != null && previous.positivePercentage != null
    ? metrics.positivePercentage - previous.positivePercentage
    : null;
  const title = period === 'yearly' ? 'Yearly emotional balance' : 'Overall mood balance this month';
  const range = formatPeriodRange(startDate, period);
  const hasTimeline = timeline.some(item => item.value != null);
  const balance = metrics.positivePercentage == null ? null : Math.round(metrics.positivePercentage);
  const mentalState = balance == null ? null : balance >= 55 ? 'Leaning positive' : balance <= 45 ? 'Leaning negative' : 'Well balanced';
  const dataSourceLabel = scopedRecords.length ? `${scopedRecords.length} dated records in this ${period === 'yearly' ? 'year' : 'month'}` : 'Using the available period aggregate';

  return (
    <div className="period-statistics">
      <section className="period-stat__hero">
        <div><span className="period-stat__eyebrow">{period === 'yearly' ? 'YEAR IN FOCUS' : 'MONTH IN FOCUS'}</span><h2><LuCalendarDays size={22} aria-hidden="true" />{range}</h2><p>{metrics.observations ? `${metrics.observations} recorded ${metrics.observations === 1 ? 'observation' : 'observations'}` : 'No recorded observations'}</p></div>
        <div className="period-stat__dominant"><span className="period-stat__emotion-icon" style={{ color: metrics.dominant.color }}><EmotionIcon emotion={metrics.dominant} size={30} /></span><div><small>Most observed</small><strong>{metrics.dominant.label}</strong>{metrics.distribution.length > 0 && <em>{metrics.dominant.percentage.toFixed(0)}% of emotional mix</em>}</div></div>
        <div className="period-stat__hero-metrics">
          {balance != null && <div><small>Balance</small><strong>{balance}% positive</strong></div>}
          {comparison && <div><small>Comparison</small><strong>{comparison}</strong>{comparisonScore != null && <em className={comparisonScore >= 0 ? 'is-positive' : 'is-negative'}>{comparisonScore >= 0 ? <LuArrowUpRight size={14} /> : <LuArrowDownRight size={14} />}{Math.abs(comparisonScore).toFixed(0)} pts</em>}</div>}
        </div>
      </section>
      <div className="period-stat__grid">
        <section className="period-stat__card period-stat__trend">
          <span className="period-stat__eyebrow">{period === 'yearly' ? 'MONTHLY PROGRESSION' : 'RECORDED OBSERVATIONS'}</span>
          <h3>{period === 'yearly' ? 'Positive emotion share by month' : 'Positive emotion share by observation date'}</h3>
          {hasTimeline ? <div className="period-stat__chart"><ResponsiveContainer width="100%" height="100%"><AreaChart data={timeline} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}><defs><linearGradient id={`period-gradient-${period}`} x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="var(--xi-violet-2)" stopOpacity={0.4} /><stop offset="100%" stopColor="var(--xi-blue)" stopOpacity={0.02} /></linearGradient></defs><XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--period-chart-axis)', fontSize: 11 }} /><YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: 'var(--period-chart-axis-muted)', fontSize: 11 }} tickFormatter={value => `${value}%`} /><Tooltip formatter={(value, name, item) => [`${Number(value).toFixed(0)}%`, `${item.payload.observations} ${item.payload.observations === 1 ? 'observation' : 'observations'}`]} /><Area type="monotone" dataKey="value" stroke="var(--xi-violet-2)" strokeWidth={3} fill={`url(#period-gradient-${period})`} connectNulls={false} dot={{ r: 4, fill: 'var(--xi-violet-2)', stroke: 'var(--period-dot-stroke)', strokeWidth: 2 }} /></AreaChart></ResponsiveContainer></div> : <div className="period-stat__empty">No dated observations are available for this period.</div>}
        </section>
        <EmotionDonut metrics={metrics} />
        <Balance metrics={metrics} title={title} />
        <section className="period-stat__card period-stat__state"><div><span className="period-stat__eyebrow">MENTAL STATE</span><h3>{mentalState || 'No state available'}</h3><p>Reflects your recorded emotional mix and is not a clinical assessment.</p></div>{balance != null && <div className="period-stat__radial" style={{ '--period-score': `${balance}%` }}><strong>{balance}</strong><span>balance</span></div>}</section>
      </div>
      <section className="period-stat__card period-stat__insight"><span className="period-stat__eyebrow">{period === 'yearly' ? 'YEARLY SUMMARY' : 'MONTHLY SUMMARY'}</span><h3>{metrics.distribution.length ? `${metrics.dominant.label} leads your recorded emotions` : 'No pattern available yet'}</h3>{metrics.distribution.length ? <p>Across {metrics.observations} recorded {metrics.observations === 1 ? 'observation' : 'observations'}, {metrics.dominant.label.toLowerCase()} appeared most often at {metrics.dominant.percentage.toFixed(0)}%. {metrics.leastFrequent && metrics.leastFrequent.key !== metrics.dominant.key ? `${metrics.leastFrequent.label} was least frequent at ${metrics.leastFrequent.percentage.toFixed(0)}%.` : ''} {dataSourceLabel}. </p> : <p>Record a session to build a data-backed summary.</p>}</section>
    </div>
  );
}
